import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TranslocoService } from "@ngneat/transloco";
import { ToastrService } from "ngx-toastr";
import { catchError, from, Observable, switchMap, throwError } from "rxjs";
import { whiteLabelDspText } from "../shared/utils/dsp-white-label.util";
import { ApiError, DSP_CREDENTIALS_EXPIRED, DSP_CREDENTIALS_INVALID, DSP_CREDENTIALS_MISSING } from "./api-error";

@Injectable({
    providedIn: "root",
})
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private _toastr: ToastrService,
        private _router: Router,
        private _translocoService: TranslocoService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                // Si es un error del diccionario, auth (V1 o V2), auth/refresh o
                // geo-audience/service/auth, dejarlo pasar sin procesar para que el
                // componente reciba el HttpErrorResponse original (status, body...).
                //
                // unique-users también se deja pasar: reportsanalytics-v2 necesita
                // el status/body reales (`error.error`) para distinguir un 400 por
                // el retention floor del DSP (recuperable con un retry) de un
                // fallo genuino — este interceptor sustituye cualquier otro error
                // por un `Error` plano sin status ni body, lo cual rompería ese
                // retry.
                //
                // nominatim.openstreetmap.org también: es un servicio de terceros,
                // no nuestro backend — la lógica de retry/circuit-breaker de
                // GeocodingService necesita `error.status` real (0 = CORS/bloqueo
                // de Nominatim vs 429 = rate limit genuino) para decidir si
                // reintentar tiene sentido. Verificado en vivo: sin esta excepción
                // `error.status` llegaba `undefined` (el `Error` plano de abajo no
                // tiene esa propiedad), así que ninguna rama de ese retry/circuit
                // breaker se activaba nunca — cada ciudad agotaba sus reintentos
                // igual, con o sin el fix.
                if (
                    error.url?.includes("/api/dictionary") ||
                    error.url?.includes("/api/auth/login") ||
                    error.url?.includes("/api/auth/refresh") ||
                    error.url?.includes("/api/v2/auth/") ||
                    error.url?.includes("/api/v1/notifications") ||
                    error.url?.includes("/geo-audience/service") ||
                    error.url?.includes("/api/v2/analytics/unique-users") ||
                    error.url?.includes("nominatim.openstreetmap.org")
                ) {
                    return throwError(() => error);
                }

                // Requests made with responseType "blob"/"text" (file downloads) never get
                // their error body auto-parsed as JSON by HttpClient — `error.error` arrives
                // as a raw Blob, so `.message` below would always be undefined. Read it as
                // text and parse it before building the error response, so the real backend
                // message survives instead of silently collapsing to the generic fallback.
                if (error.error instanceof Blob) {
                    return from(error.error.text()).pipe(
                        switchMap((text) => {
                            let parsedError: any = null;
                            try {
                                parsedError = JSON.parse(text);
                            } catch {
                                // Body wasn't JSON — leave parsedError null, fallback message applies below.
                            }
                            return this._handleError(
                                new HttpErrorResponse({
                                    error: parsedError,
                                    headers: error.headers,
                                    status: error.status,
                                    statusText: error.statusText,
                                    url: error.url ?? undefined,
                                })
                            );
                        })
                    );
                }

                return this._handleError(error);
            })
        );
    }

    private _handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage: string = this._translocoService.translate("An unexpected error occurred, please try again later");

        if (error.status) {
            errorMessage += ` - [${error.status}] `;
        }

        // TODO: SI NO CANTA MUCHO EN LA APP, DEJAR EL ERROR MESSAGE VACÍO DE INICIO, SIN LAS LINEAS PREVIAS.
        errorMessage = "";

        if (error.error?.message) {
            errorMessage += error.error.message;
        }

        // Detectar errores del DSP Service y mostrar mensaje específico
        const dspErrorRegex = /\(DSP Service\)/i;
        if (dspErrorRegex.test(error.error?.message)) {
            const dspMessage = error.error.message.replace(/^\(DSP Service\)\s*/i, "");
            errorMessage = dspMessage;
        }

        // La marca blanca de los DSP no se aplica aquí sino en `_throwApiError`, sobre el mensaje
        // final: las ramas de abajo todavía pueden volver a poner el texto crudo del backend.

        // Un `error_code` marca un error que la API tiene previsto (DSP_UNAVAILABLE,
        // DSP_ACTION_NOT_SUPPORTED, DSP_TIMEOUT, DSP_CANCEL_FAILED, credenciales del DSP...) y que
        // llega con un mensaje para el usuario. En un 504 DSP_TIMEOUT ese mensaje dice "no lo
        // reintentes" y en un 502 DSP_CANCEL_FAILED "no se ha borrado": el genérico de abajo, que
        // invita a reintentar, diría justo lo contrario.
        const errorCode: string | null = error.error?.error_code ?? null;

        // Errores de servidor (5xx) sin un mensaje útil del DSP: mostrar un mensaje
        // amable y tranquilizador en lugar del "Server Error" crudo de Laravel. Con un
        // error_code el mensaje de la API ya es el útil, así que se conserva.
        const isFriendly5xx =
            error.status >= 500 && !dspErrorRegex.test(error.error?.message) && !(errorCode && error.error?.message);
        if (isFriendly5xx) {
            errorMessage = this._translocoService.translate(
                "Something went wrong on our side. No need to worry — please try again in a moment."
            );
        }

        // Credenciales del DSP caducadas/inválidas (409 + error_code del backend):
        // mensaje específico y accionable en lugar de cualquier texto genérico.
        if (errorCode === DSP_CREDENTIALS_EXPIRED) {
            errorMessage = this._translocoService.translate(
                "Your organization's DSP password has expired. Renew the credentials to resume campaign management."
            );
        } else if (errorCode === DSP_CREDENTIALS_INVALID) {
            errorMessage = this._translocoService.translate(
                "Your organization's DSP credentials are no longer valid. Renew them to resume campaign management."
            );
        } else if (errorCode === DSP_CREDENTIALS_MISSING) {
            errorMessage = this._translocoService.translate("dsp_credentials.missing");
        }

        // Rate limit (429): only Laravel's throttle text ("Too Many Attempts.") is replaced. Other 429s
        // (duplication lock, DSP, audience sync) carry their own meaning and must keep their message.
        const rawMessage = (error.error?.message ?? "").trim();
        const isRateLimited = error.status === 429 && (rawMessage === "" || rawMessage === "Too Many Attempts.");
        if (isRateLimited) {
            errorMessage = this._translocoService.translate("errors.too_many_requests");
        }

        switch (error.status) {
            case 500:
                break; // TODO: REVISAR CUANDO QUEREMOS CORTAR EL FLUJO DE USUARIO EN ESTE ERROR...
                this._router.navigate(["/error/500"]);
                break;
            case 503:
                // Con error_code (p. ej. DSP_UNAVAILABLE al crear una DOOH sin credenciales del DSP) no es
                // una caída: el componente enseña el mensaje y el usuario no pierde el formulario.
                if (!errorCode) {
                    this._router.navigate(["/error/503"]);
                }
                break;
        }

        console.error("Error Interceptor:", error);

        // Detener ejecución en caso de 404, 500, 503
        if ([404, 500, 503].includes(error.status)) {
            return this._throwApiError(errorMessage, error, errorCode);
        }

        // Solo usar el mensaje original si no hemos procesado un error DSP ni
        // fijado ya un mensaje específico (5xx amable o credenciales del DSP):
        // sin estas guardas, un 502/504 pisaba el mensaje amable con el crudo.
        if (!dspErrorRegex.test(error.error?.message) && !isFriendly5xx && !isRateLimited && !errorCode) {
            errorMessage = error?.error?.message ?? errorMessage;
        }
        return this._throwApiError(errorMessage, error, errorCode);
    }

    /**
     * Único punto de salida de `_handleError`: todas las ramas devuelven el error por aquí.
     *
     * La marca blanca de los DSP (MediaSmart → "GeoActivate", Perion/Hivestack → "DOOH Direct",
     * sin sus URLs) se aplica sobre el mensaje FINAL. Antes se hacía a mitad del método y la rama
     * que recupera `error.error.message` volvía a poner el texto crudo, así que todos los
     * 400/409/422 enseñaban el nombre del proveedor. `body` sigue siendo el cuerpo original, para
     * quien necesite ramificar sobre él.
     */
    private _throwApiError(message: string, error: HttpErrorResponse, errorCode: string | null): Observable<never> {
        return throwError(() => new ApiError(whiteLabelDspText(message), error.status, errorCode, error.error));
    }
}
